import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import gateEntryApis from "../../../api/master/gateEntry";
import sites from "../../../api/master/sites";
import sequenceApis from "../../../api/master/sequence";
import Swal from "sweetalert2";

interface SelectOptions {
  label: string;
  value: string | number;
}

interface GateEntryItem {
  gateEntryNumber: string;
  description: string;
  gateEntryId?: string;
}

const GateEntryForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
  isSequenceSet?: boolean;
}> = ({ id, formAction, toggleModelPopUp, refreshData, isSequenceSet = false }) => {
  const [action, setAction] = useState(formAction);
  const [gateEntryDetail, setGateEntryDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState<SelectOptions[]>([]);
  const [localIsSequenceSet, setLocalIsSequenceSet] = useState(false);
  const [gateEntryItems, setGateEntryItems] = useState<GateEntryItem[]>([
    { gateEntryNumber: "", description: "" },
  ]);
  const [alertInfo, setAlertInfo] = useState<{
    type: string;
    message: string;
    show: boolean;
  }>({
    type: "",
    message: "",
    show: false,
  });

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, watch, getValues } = methods;

  const fetchSequenceStatus = async () => {
    try {
      const response = await sequenceApis.fetchSequenceByModuleId(179);
      const { data } = response;
      const today = new Date();
      // Check if sequence exists with:
      // 1. moduleId === 179 (Gate Entry)
      // 2. status === "Active" (if "Active", hide Gate Entry ID field)
      // 3. endDate is in the future
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleId === 179 &&
          item.status === "Active" &&
          new Date(item.endDate) > today
      );
      setLocalIsSequenceSet(_sequenceAccess && _sequenceAccess.length > 0);
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setLocalIsSequenceSet(false);
    }
  };

  // Fetch sequence status when form opens (especially for add action)
  useEffect(() => {
    if (action === "add") {
      fetchSequenceStatus();
    }
  }, [action]);

  // Sync gateEntryItems state with form values
  useEffect(() => {
    gateEntryItems.forEach((item, index) => {
      setValue(`gateEntryNumber_${index}`, item.gateEntryNumber || "", { shouldDirty: false });
      setValue(`description_${index}`, item.description || "", { shouldDirty: false });
      // Sync gateEntryId for additional items when sequence is not set
      const sequenceSet = localIsSequenceSet || isSequenceSet;
      if (!sequenceSet && index > 0 && item.gateEntryId !== undefined) {
        setValue(`gateEntryId_${index}`, item.gateEntryId || "", { shouldDirty: false });
      }
    });
  }, [gateEntryItems, setValue, localIsSequenceSet, isSequenceSet]);

  const fetchSites = async () => {
    try {
      const response = await sites.fetchSites();
      if (response?.status) {
        const sitesList = response.data.map((site: any) => ({
          label: site.name,
          value: site.siteID.toString(),
        }));
        setSiteOptions(sitesList);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  };

  const handleAddGateEntryItem = () => {
    const sequenceSet = localIsSequenceSet || isSequenceSet;
    setGateEntryItems([
      ...gateEntryItems,
      { 
        gateEntryNumber: "", 
        description: "",
        ...(!sequenceSet && { gateEntryId: "" })
      },
    ]);
  };

  const handleRemoveGateEntryItem = (index: number) => {
    if (gateEntryItems.length > 1) {
      const updatedItems = gateEntryItems.filter((_, i) => i !== index);
      setGateEntryItems(updatedItems);
    }
  };

  const handleGateEntryItemChange = (
    index: number,
    field: keyof GateEntryItem,
    value: string
  ) => {
    const updatedItems = [...gateEntryItems];
    updatedItems[index][field] = value;
    setGateEntryItems(updatedItems);
  };

  const fetchGateEntryDetails = async (gateEntryId: any) => {
    try {
      setLoading(true);
      const response = await gateEntryApis.fetchGateEntryById(gateEntryId);
      if (response?.status) {
        const data = response.data;
        setGateEntryDetail(data);
        setValue("siteId", data.siteId);
        setValue("description", data.description || "");

        // Set gateEntryId if sequence is not set (inactive)
        const sequenceSet = localIsSequenceSet || isSequenceSet;
        if (!sequenceSet && data.gateEntryId) {
          setValue("gateEntryId", data.gateEntryId);
        }

        // Load gate entry items if they exist
        if (data.gateEntryItems && data.gateEntryItems.length > 0) {
          setGateEntryItems(data.gateEntryItems);
        } else {
          // Initialize with at least one item if none exist
          setGateEntryItems([
            {
              gateEntryNumber: data.gateEntryNumber || "",
              description: data.description || "",
            },
          ]);
        }
      } else {
        showAlertMessage("error", "Failed to load gate entry details.");
      }
    } catch (error) {
      showAlertMessage("error", "Error fetching gate entry details.");
    } finally {
      setLoading(false);
    }
  };

  const showAlertMessage = (type: string, message: string) => {
    setAlertInfo({ type, message, show: true });
  };

  const hideAlert = () => {
    setAlertInfo((prev) => ({ ...prev, show: false }));
  };

  const hasFormChanges = () => {
    if (action !== "edit" || !id) return true;
    if (!gateEntryDetail || Object.keys(gateEntryDetail).length === 0) return true;
    const current = getValues();
    const currentValues = {
      siteId: current.siteId || "",
      description: current.description || "",
      gateEntryItems: gateEntryItems,
    };

    const originalValues = {
      siteId: gateEntryDetail.siteId || "",
      description: gateEntryDetail.description || "",
      gateEntryItems: gateEntryDetail.gateEntryItems || [],
    };

    return (
      currentValues.siteId !== originalValues.siteId ||
      currentValues.description !== originalValues.description ||
      JSON.stringify(currentValues.gateEntryItems) !== JSON.stringify(originalValues.gateEntryItems)
    );
  };

  const onSubmit = async (_data: any) => {
    try {
      setLoading(true);
      hideAlert();
      const current = getValues();

      // Use local sequence status if available, otherwise fall back to prop
      const sequenceSet = localIsSequenceSet || isSequenceSet;

      // Validate that at least one gate entry item has a gate entry number
      const validItems = gateEntryItems
        .map((item, index) => {
          const itemData: any = {
            gateEntryNumber: item.gateEntryNumber,
            description: item.description || "",
          };
          
          // Include gateEntrySequenceId when sequence is set (optional, backend may generate it)
          if (sequenceSet) {
            // If gateEntrySequenceId is provided in the form, use it
            const sequenceId = current[`gateEntrySequenceId_${index}`] || current.gateEntrySequenceId;
            if (sequenceId) {
              itemData.gateEntrySequenceId = sequenceId;
            }
          } else {
            // When sequence is NOT set (inactive), use the same form fields
            // but map them to gateEntrySequenceId for the API payload
            if (index === 0) {
              // First item uses the main gateEntryId field
              itemData.gateEntrySequenceId = current.gateEntryId || "";
            } else {
              // Additional items use their own gateEntryId field
              itemData.gateEntrySequenceId = current[`gateEntryId_${index}`] || "";
            }
          }
          
          return itemData;
        })
        .filter((item) => {
          // Filter items that have gate entry number
          const hasGateEntryNumber = item.gateEntryNumber && item.gateEntryNumber.trim() !== "";
          
          // If sequence is not set, also validate that gateEntrySequenceId (from Gate Entry ID fields) is provided
          if (!sequenceSet) {
            const hasGateEntrySequenceId =
              item.gateEntrySequenceId && item.gateEntrySequenceId.trim() !== "";
            return hasGateEntryNumber && hasGateEntrySequenceId;
          }
          
          return hasGateEntryNumber;
        });
      
      if (validItems.length === 0) {
        const errorMsg = !sequenceSet
          ? "Please add at least one gate entry with both Gate Entry ID and Gate Entry Number."
          : "Please add at least one gate entry number.";
        showAlertMessage("error", errorMsg);
        setLoading(false);
        return;
      }

      // For create action, use the new payload structure
      const submitData: any = action === "add" ? {
        siteId: Number(current.siteId),
        gateEntries: validItems.map((item) => {
          const entry: any = {
            gateEntryNumber: item.gateEntryNumber,
            description: item.description || "",
          };

          // Always send gateEntrySequenceId when present (for both sequence modes)
          if (item.gateEntrySequenceId) {
            entry.gateEntrySequenceId = item.gateEntrySequenceId;
          }

          return entry;
        }),
      } : {
        // For edit, keep the old structure (or update based on edit API requirements)
        siteId: current.siteId,
        description: current.description || "",
        gateEntryItems: validItems,
      };

      if (action === "edit" && id) {
        if (
          gateEntryDetail &&
          Object.keys(gateEntryDetail).length > 0 &&
          !hasFormChanges()
        ) {
          await Swal.fire({
            title: "No Changes",
            text: "No fields were modified.",
            icon: "info",
            confirmButtonText: "OK",
          });
          setLoading(false);
          return;
        }
      }

      let response;

      if (action === "edit" && id) {
        response = await gateEntryApis.updateGateEntryById(
          Number(id),
          submitData
        );
      } else {
        response = await gateEntryApis.createGateEntry(submitData);
      }

      if (response?.status) {
        toggleModelPopUp();
        
        await Swal.fire({
          title: "Success!",
          text:
            action === "edit"
              ? "Gate entry has been updated"
              : "Gate entry has been created",
          icon: "success",
          confirmButtonText: "OK",
        });
        
        if (refreshData) {
          refreshData();
        }
      } else {
        const msg = response?.message || "Something went wrong!";
        await Swal.fire({
          title: "Error!",
          text: msg,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message || "An error occurred while saving";
      await Swal.fire({
        title: "Error!",
        text: errMsg,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
    if (id && action === "edit") {
      fetchGateEntryDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, action]);

  useEffect(() => {
    return () => {
      hideAlert();
    };
  }, []);

  if (
    loading &&
    action === "edit" &&
    id &&
    (!gateEntryDetail || Object.keys(gateEntryDetail).length === 0)
  ) {
    return (
      <div className="text-center p-4">
        <Icon name="spinner" className="spin" /> Loading...
      </div>
    );
  }

  return (
    <>
      <Alert
        color={alertInfo.type === "error" ? "danger" : "success"}
        className="alert-icon"
        isOpen={alertInfo.show}
        style={{
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
          marginBottom: "1rem",
          marginTop: "0.5rem",
        }}
      >
        <Icon
          name={alertInfo.type === "error" ? "alert-circle" : "check-circle"}
        />
        <span>{alertInfo.message}</span>
      </Alert>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={4}>
              <FormSelect
                name="siteId"
                label="Site Name"
                options={siteOptions}
                required={true}
                placeholder="Select Site"
                disabled={action === "edit"}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            {/* Show Gate Entry ID field when sequence is NOT set (inactive) */}
            {!(localIsSequenceSet || isSequenceSet) && (
              <Col md={3}>
                <FormInput
                  name="gateEntryId"
                  label="Gate Entry ID"
                  type="text"
                  placeholder={action === "edit" ? "" : "Enter Gate Entry ID"}
                  defaultValue={
                    action === "edit" && gateEntryDetail.gateEntryId
                      ? gateEntryDetail.gateEntryId
                      : action === "edit" && id
                        ? id
                        : ""
                  }
                  disabled={action === "edit"}
                  required={action === "add" ? true : false}
                  onChange={(e) => {
                    if (action === "add") {
                      setValue("gateEntryId", e.target.value);
                    }
                  }}
                />
              </Col>
            )}
            <Col md={!(localIsSequenceSet || isSequenceSet) ? 3 : 4}>
              <FormInput
                name="gateEntryNumber_0"
                label="Gate Entry Number"
                type="text"
                placeholder="Enter Gate Entry Number"
                required={true}
                onChange={(e) =>
                  handleGateEntryItemChange(0, "gateEntryNumber", e.target.value)
                }
              />
            </Col>
            <Col md={!(localIsSequenceSet || isSequenceSet) ? 6 : 8} style={{ position: "relative" }}>
              <FormInput
                name="description_0"
                label="Description"
                type="text"
                placeholder="Enter Description"
                required={false}
                onChange={(e) =>
                  handleGateEntryItemChange(0, "description", e.target.value)
                }
              />
            </Col>
          </Row>

          {gateEntryItems.slice(1).map((item, index) => {
            const actualIndex = index + 1;
            return (
              <Row key={actualIndex} className="mt-2">
                {/* Show Gate Entry ID field when sequence is NOT set (inactive) */}
                {!(localIsSequenceSet || isSequenceSet) && (
                  <Col md={3}>
                    <FormInput
                      name={`gateEntryId_${actualIndex}`}
                      label="Gate Entry ID"
                      type="text"
                      placeholder="Enter Gate Entry ID"
                      required={true}
                      onChange={(e) => {
                        setValue(`gateEntryId_${actualIndex}`, e.target.value);
                      }}
                    />
                  </Col>
                )}
                <Col md={!(localIsSequenceSet || isSequenceSet) ? 3 : 4}>
                  <FormInput
                    name={`gateEntryNumber_${actualIndex}`}
                    label="Gate Entry Number"
                    type="text"
                    placeholder="Enter Gate Entry Number"
                    required={true}
                    onChange={(e) =>
                      handleGateEntryItemChange(
                        actualIndex,
                        "gateEntryNumber",
                        e.target.value
                      )
                    }
                  />
                </Col>
                <Col md={!(localIsSequenceSet || isSequenceSet) ? 6 : 8} style={{ position: "relative" }}>
                  <FormInput
                    name={`description_${actualIndex}`}
                    label="Description"
                    type="text"
                    placeholder="Enter Description"
                    required={false}
                    onChange={(e) =>
                      handleGateEntryItemChange(
                        actualIndex,
                        "description",
                        e.target.value
                      )
                    }
                  />
                  <Button
                    type="button"
                    color="danger"
                    outline
                    size="sm"
                    onClick={() => handleRemoveGateEntryItem(actualIndex)}
                    style={{
                      position: "absolute",
                      top: "0",
                      right: "0",
                      padding: "4px 8px",
                      minWidth: "auto",
                      border: "none",
                    }}
                  >
                    <Icon name="trash"></Icon>
                  </Button>
                </Col>
              </Row>
            );
          })}

          <Row className="mt-2">
            <Col lg="12" className="d-flex justify-content-end">
              <a
                onClick={handleAddGateEntryItem}
                href="#add"
                className="addNewItem"
                style={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.6 : 1 }}
              >
                <Icon name="plus-round" />
                <span>Add New</span>
              </a>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    <span>{action === "edit" ? "Update" : "Submit"}</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(ev) => {
                      ev.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default GateEntryForm;

